"use client"

import { useState } from "react"
import { AlertCircle, Copy, Check } from "lucide-react"
import { colorizeJson } from "../utils/colorize-json"

function JsonFormatter() {
  const [input, setInput] = useState("")
  const [output, setOutput] = useState("")
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)
  const [spaces, setSpaces] = useState(2)

  const formatJson = () => {
    try {
      if (!input.trim()) {
        setOutput("")
        setError(null)
        return
      }

      // Parse the JSON to validate it
      const parsedJson = JSON.parse(input)

      // Stringify with proper indentation
      const formattedJson = JSON.stringify(parsedJson, null, spaces)

      setOutput(formattedJson)
      setError(null)
    } catch (err) {
      setError(err.message)
      setOutput("")
    }
  }

  const copyToClipboard = () => {
    if (output) {
      navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const clearAll = () => {
    setInput("")
    setOutput("")
    setError(null)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-4xl p-4 md:p-8">
        <h1 className="mb-6 text-2xl font-bold text-gray-800">JSON Formatter</h1>

        <div className="mb-4 flex flex-wrap gap-4">
          <button
            onClick={formatJson}
            className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Format JSON
          </button>

          <button
            onClick={clearAll}
            className="rounded border border-gray-300 bg-white px-4 py-2 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Clear
          </button>

          <div className="flex items-center">
            <label htmlFor="spaces" className="mr-2 text-sm text-gray-700">
              Indent:
            </label>
            <select
              id="spaces"
              value={spaces}
              onChange={(e) => setSpaces(Number(e.target.value))}
              className="rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value={2}>2 spaces</option>
              <option value={4}>4 spaces</option>
              <option value={8}>8 spaces</option>
            </select>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col">
            <label htmlFor="input" className="mb-2 text-sm font-medium text-gray-700">
              Input JSON:
            </label>
            <textarea
              id="input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Paste your JSON here..."
              className="h-80 resize-none rounded border border-gray-300 p-3 font-mono text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-col">
            <div className="mb-2 flex items-center justify-between">
              <label htmlFor="output" className="text-sm font-medium text-gray-700">
                Formatted JSON:
              </label>
              {output && (
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-1 rounded-md p-1 text-xs text-gray-500 hover:bg-gray-100"
                  aria-label="Copy to clipboard"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              )}
            </div>
            <div className="relative h-80">
              <pre
                id="output"
                className={`h-full w-full overflow-auto rounded border p-3 font-mono text-sm ${
                  error ? "border-red-300 bg-red-50" : "border-gray-300 bg-gray-50"
                }`}
              >
                {error ? (
                  <div className="flex items-start gap-2 text-red-600">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>Error: {error}</span>
                  </div>
                ) : output ? (
                  colorizeJson(output)
                ) : (
                  <span className="text-gray-400">Formatted JSON will appear here...</span>
                )}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default JsonFormatter
